if (!customElements.get('product-info')) {
  customElements.define('product-info', class ProductInfo extends HTMLElement {
    quantityInput = undefined;
    quantityForm = undefined;
    onVariantChangeUnsubscriber = undefined;
    cartUpdateUnsubscriber = undefined;
    abortController = undefined;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];
    variantRequestId = 0;
    mediaSyncToken = 0;

    constructor() {
      super();
      this.quantityInput = this.querySelector('.quantity__input');
    }

    connectedCallback() {
      this.initializeProductSwapUtility();

      this.onVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.initQuantityHandlers();
      this.initOptionHoverPreload();
      this.initStickyAccordions();
      this.syncInitialMedia();
      this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
    }

    initStickyAccordions() {
      const stickyColumn = this.querySelector('.product__info-container.product__column-sticky');
      if (!stickyColumn) return;

      this.stickyDesktop = window.matchMedia('(min-width: 577px)');

      stickyColumn.addEventListener('change', (event) => {
        const checkbox = event.target;
        if (!checkbox.matches?.('.details__checkbox') || !checkbox.checked) return;
        if (!this.stickyDesktop.matches) return;

        const details = checkbox.closest('.details');
        const summary = details?.querySelector('.details__summary');
        if (!details || !summary) return;

        this.revealOpenedAccordion(details, summary, stickyColumn);
      });
    }

    getStickyOffset(stickyColumn) {
      const columnTop = parseFloat(getComputedStyle(stickyColumn).top) || 0;
      const hasStickyHeader = !!document.querySelector('sticky-header');
      if (!hasStickyHeader) return columnTop;

      const headerHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      ) || 0;
      return Math.max(columnTop, headerHeight + 40);
    }

    revealOpenedAccordion(details, summary, stickyColumn) {
      const align = () => {
        const infoHeight = stickyColumn.offsetHeight;
        const offset = this.getStickyOffset(stickyColumn);
        const fitsViewport = infoHeight <= window.innerHeight - offset;
        const headerTop = summary.getBoundingClientRect().top;
        const delta = headerTop - offset;

        if (fitsViewport) return;

        if (headerTop < offset - 1) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollBy({ top: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      };

      let settled = false;
      const onEnd = (event) => {
        if (event.target !== details || event.propertyName !== 'grid-template-rows') return;
        settled = true;
        details.removeEventListener('transitionend', onEnd);
        requestAnimationFrame(align);
      };
      details.addEventListener('transitionend', onEnd);

      setTimeout(() => {
        if (settled) return;
        details.removeEventListener('transitionend', onEnd);
        align();
      }, 400);
    }

    initOptionHoverPreload() {
      const mediaGallery = this.querySelector('media-gallery');
      if (!mediaGallery) return;

      const optionLabels = this.querySelectorAll('.product-form__colors label');

      optionLabels.forEach((label) => {
        label.addEventListener('mouseenter', () => {
          const input = document.getElementById(label.getAttribute('for'));
          if (!input) return;

          const mediaId = input.dataset.mediaId;
          if (mediaId) {
            mediaGallery.preloadThumbnailImage?.(mediaId);
          }
        });
      });
    }

    addPreProcessCallback(callback) {
      this.preProcessHtmlCallbacks.push(callback);
    }

    initQuantityHandlers() {
      if (!this.quantityInput) return;

      this.quantityForm = this.querySelector('.product-form__quantity');
      if (!this.quantityForm) return;

      this.setQuantityBoundries();
      if (!this.dataset.originalSection) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
      }
    }

    disconnectedCallback() {
      this.onVariantChangeUnsubscriber();
      this.cartUpdateUnsubscriber?.();
    }

    initializeProductSwapUtility() {
      this.preProcessHtmlCallbacks.push((html) =>
        html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
      );
      this.postProcessHtmlCallbacks.push((newNode) => {
        window?.Shopify?.PaymentButton?.init();
        window?.ProductModel?.loadShopifyXR();
      });
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      const targetProductCard = event.target.closest('product-card');
      if (targetProductCard?.dataset.section === 'complementary-products') return;

      const optionInputRoot = event.target.closest('.product-form__input');
      const isColorChange = Boolean(
        event.target.closest('.product-form__colors') ||
        optionInputRoot?.querySelector('.product-form__colors')
      );

      this.resetProductFormState();
      const requestId = ++this.variantRequestId;
      this.mediaSyncToken = requestId;
      const requestUrl = this.buildRequestUrlWithParams(this.dataset.url, selectedOptionValues);

      this.renderProductInfo({
        requestUrl,
        requestId,
        targetId: target.id,
        callback: this.handleUpdateProductInfo(isColorChange, requestId),
      });
    }

    resetProductFormState() {
      const productForm = this.productForm;
      productForm?.toggleSubmitButton?.(true);
      productForm?.handleErrorMessage?.();
    }

    renderProductInfo({ requestUrl, requestId, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      const normalizedTargetId = targetId?.trim();

      fetch(requestUrl, { signal })
        .then((response) => response.text())
        .then((responseText) => {
          if (signal.aborted || requestId !== this.variantRequestId) return false;
          const html = this.parseSectionHtml(responseText);
          return callback(html);
        })
        .then((didApply) => {
          if (!didApply || signal.aborted || requestId !== this.variantRequestId) return;
          const nextTarget = normalizedTargetId ? document.getElementById(normalizedTargetId) : null;
          nextTarget?.focus();
          if (window.tooltipInstance) {
            window.tooltipInstance.init(this);
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error(error);
          }
        });
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
      if (!selectedVariant) return null;
      try {
        return JSON.parse(selectedVariant);
      } catch (error) {
        return null;
      }
    }

    buildRequestUrlWithParams(url, optionValues) {
      const params = [`section_id=${this.sectionId}`];

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}${params.join('&')}`;
    }

    syncInitialMedia() {
      requestAnimationFrame(() => {
        const mediaGallery = this.querySelector('media-gallery');
        if (mediaGallery?.hasAttribute('data-default-first-image')) return;
        const variant = this.getSelectedVariant(this);
        const targetMediaId = variant?.featured_media?.id ? `${this.sectionId}-${variant.featured_media.id}` : null;

        if (!mediaGallery || !targetMediaId) return;

        const activeSlide =
          mediaGallery.querySelector('[data-media-id].slide--active') ||
          mediaGallery.querySelector('[data-media-id]');

        if (activeSlide?.dataset?.mediaId === targetMediaId) return;

        mediaGallery.setActiveMedia?.(targetMediaId, false, false);
      });
    }

    updateOptionValues(html) {
      const newVariantSelects = html.querySelector('variant-selects');
      const currentVariantSelects = this.variantSelectors;
      if (!newVariantSelects || !currentVariantSelects) return;

      const currentFieldsets = currentVariantSelects.querySelectorAll('fieldset.product-form__input, .product-form__input:not(fieldset)');
      const newFieldsets = newVariantSelects.querySelectorAll('fieldset.product-form__input, .product-form__input:not(fieldset)');

      currentFieldsets.forEach((fieldset, i) => {
        if (i === 0 || !newFieldsets[i]) return;

        const currentSelect = fieldset.querySelector('select');
        const newSelect = newFieldsets[i].querySelector('select');

        if (currentSelect && newSelect) {
          currentSelect.innerHTML = newSelect.innerHTML;
          currentSelect.value = newSelect.value;
        } else if (!currentSelect && !newSelect) {
          const currentGrid = fieldset.querySelector('.product-form__options-grid');
          const newGrid = newFieldsets[i].querySelector('.product-form__options-grid');
          if (currentGrid && newGrid) currentGrid.innerHTML = newGrid.innerHTML;
        }

        const currentValue = fieldset.querySelector('[data-selected-value]');
        const newValue = newFieldsets[i].querySelector('[data-selected-value]');
        if (currentValue && newValue) currentValue.textContent = newValue.textContent;
      });

      const currentScript = currentVariantSelects.querySelector('[data-selected-variant]');
      const newScript = newVariantSelects.querySelector('[data-selected-variant]');
      if (currentScript && newScript) currentScript.innerHTML = newScript.innerHTML;
    }

    triggerSlideAnimation(mediaGallery, targetMediaId) {
      const animatedStages = this.querySelector('animated-stages');
      if (!animatedStages || !window.motion?.animate) return;
      const slide = mediaGallery?.querySelector(`[data-media-id="${targetMediaId}"]`) || this.querySelector(`[data-media-id="${targetMediaId}"]`);
      const animatedImageContainer = slide?.matches('[data-animate="image"]') ? slide : slide?.querySelector('[data-animate="image"]');
      if (animatedImageContainer) animatedStages.animateImage(window.motion.animate, animatedImageContainer);
    }

    normalizeImageUrl(url) {
      if (!url) return '';
      try {
        const parsed = new URL(url, window.location.origin);
        return `${parsed.origin}${parsed.pathname}`;
      } catch (_) {
        return url.split('?')[0];
      }
    }

    getSlideImageSrc(slide) {
      if (!slide) return '';
      const img = slide.querySelector('img');
      if (!img) return '';
      return img.currentSrc || img.src || img.dataset?.src || '';
    }

    syncMedia(mediaGallery, variant, html, isMobile, isColorChange = false, requestId = this.variantRequestId) {
      if (!mediaGallery || !variant?.featured_media?.id) return;

      const targetMediaId = `${this.sectionId}-${variant.featured_media.id}`;
      const previousColorTargetMediaId = this.lastColorTargetMediaId || null;
      const isNewColorTarget = isColorChange && previousColorTargetMediaId !== targetMediaId;
      const currentSlide = mediaGallery?.querySelector('[data-media-id].slide--active')
        || mediaGallery?.querySelector('[data-media-id]');
      const currentMediaId = currentSlide?.dataset?.mediaId || '';
      const currentSrc = this.getSlideImageSrc(currentSlide);
      const targetSlide = html.querySelector(`[data-media-id="${targetMediaId}"]`);
      const targetSrc = this.getSlideImageSrc(targetSlide);
      const normalizedCurrentSrc = this.normalizeImageUrl(currentSrc);
      const normalizedTargetSrc = this.normalizeImageUrl(targetSrc);
      const isSameMedia =
        currentMediaId === targetMediaId ||
        (normalizedCurrentSrc && normalizedTargetSrc && normalizedCurrentSrc === normalizedTargetSrc);

      if (isSameMedia) {
        const isMobileSmall = DeviceUtils.isMobileSmall;
      
        const isQuickViewColumnLayout =
        mediaGallery?.closest('quickview-modal')
          ? (mediaGallery?.dataset?.desktopLayout === 'one-column' || mediaGallery?.dataset?.desktopLayout === 'two-columns')
          : (!isMobileSmall && mediaGallery?.dataset?.desktopLayout === 'one-column');

        if (isQuickViewColumnLayout) {
          mediaGallery.setActiveMedia?.(targetMediaId, false, false);
        }
        if (isNewColorTarget) {
          this.triggerSlideAnimation(mediaGallery, targetMediaId);
        }
        if (isColorChange) {
          this.lastColorTargetMediaId = targetMediaId;
        }
        this.updateMedia(html, variant.featured_media.id);
        return;
      }

      const shouldScroll = this.hasAttribute('data-scroll-active-media') && !isMobile;
      const preSwitchNormalizedSrc = normalizedCurrentSrc;
      mediaGallery?.setActiveMedia?.(targetMediaId, shouldScroll, true);
      this.updateMedia(html, variant.featured_media.id);

      requestAnimationFrame(() => {
        if (requestId !== this.variantRequestId || requestId !== this.mediaSyncToken) return;
        const activeSlideAfterSwitch =
          mediaGallery?.querySelector('[data-media-id].slide--active')
          || mediaGallery?.querySelector(`[data-media-id="${targetMediaId}"]`);
        const postSwitchNormalizedSrc = this.normalizeImageUrl(this.getSlideImageSrc(activeSlideAfterSwitch));
        const shouldAnimateByImageChange = Boolean(
          postSwitchNormalizedSrc &&
          preSwitchNormalizedSrc &&
          postSwitchNormalizedSrc !== preSwitchNormalizedSrc
        );
        const shouldAnimateByOptionType = isNewColorTarget;
        const shouldAnimate = shouldAnimateByImageChange && shouldAnimateByOptionType;

        if (shouldAnimate) {
          this.triggerSlideAnimation(mediaGallery, targetMediaId);
        }
        if (isColorChange) {
          this.lastColorTargetMediaId = targetMediaId;
        }
      });
    }

    handleUpdateProductInfo(isColorChange = false, requestId = this.variantRequestId) {
      return (html) => {
        if (requestId !== this.variantRequestId) return false;

        const variant = this.getSelectedVariant(html);

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return true;
        }

        const isMobile = DeviceUtils.isMobileSmall;
        const mediaGallery = this.querySelector('media-gallery');
        const hasMedia = Boolean(variant?.featured_media?.id);

        const isSimpleLayout =
          mediaGallery?.matches(
            '[data-desktop-layout="thumbnails-off"], [data-desktop-layout="thumbnails-horizontal"], [data-desktop-layout="thumbnails-vertical"]'
          ) || isMobile;

        if (hasMedia && isSimpleLayout) {
          if (mediaGallery?.hasAttribute('data-hidden-variants')) {
            const hiddenVariantMediaId = `${this.sectionId}-${variant.featured_media.id}`;
            const sliderComponent = mediaGallery.querySelector('slider-component');
            const firstSlide = mediaGallery?.querySelector('[data-media-id]');
            const newSlide = html.querySelector(`[data-media-id="${hiddenVariantMediaId}"]`);

            if (newSlide) {
              newSlide.querySelectorAll('[data-animate="image"]').forEach(el => el.removeAttribute('data-animate'));
              if (newSlide.matches('[data-animate="image"]')) newSlide.removeAttribute('data-animate');
            }

            const isSameMedia = firstSlide?.dataset.mediaId === hiddenVariantMediaId;

            const syncFirstSlide = (sourceSlide) => {
              if (!firstSlide || firstSlide.dataset.mediaId === hiddenVariantMediaId || !sourceSlide) return;
              firstSlide.innerHTML = sourceSlide.innerHTML;
              firstSlide.dataset.mediaId = hiddenVariantMediaId;
              const newImg = firstSlide.querySelector('img');
              if (newImg) mediaGallery.preloadImage?.(newImg);
            };

            const currentSrc = firstSlide?.querySelector('img')?.src || firstSlide?.querySelector('img')?.dataset.src || '';
            const targetSrc = newSlide?.querySelector('img')?.src || newSlide?.querySelector('img')?.dataset.src || '';

            if (!isSameMedia && currentSrc !== targetSrc && firstSlide && newSlide && sliderComponent?.fadeGoToSlide) {
              const duration = 650;
              const easing = 'cubic-bezier(0.5, 0, 0.15, 1)';
              const transitionStyle = `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}`;

              const applyLoadedImages = (el) => {
                el.querySelectorAll('img').forEach(img => {
                  if (img.dataset.src && !img.src) img.src = img.dataset.src;
                  if (img.dataset.srcset && !img.srcset) img.srcset = img.dataset.srcset;
                  img.removeAttribute('data-src');
                  img.removeAttribute('data-srcset');
                  img.removeAttribute('loading');
                });
              };

              const makeCloneStyle = (overrides) => ({
                position: 'absolute', top: '0', left: '0',
                width: `${firstSlide.offsetWidth}px`,
                height: `${firstSlide.offsetHeight}px`,
                transition: transitionStyle,
                pointerEvents: 'none',
                ...overrides,
              });

              const currentClone = newSlide.cloneNode(true);
              const targetClone = newSlide.cloneNode(true);
              applyLoadedImages(targetClone);

              currentClone.innerHTML = firstSlide.innerHTML;
              applyLoadedImages(currentClone);

              Object.assign(currentClone.style, makeCloneStyle({ zIndex: '3', opacity: '1' }));
              Object.assign(targetClone.style, makeCloneStyle({ zIndex: '4', opacity: '0', transform: 'scale(1.25)' }));

              sliderComponent.sliderContainer.append(currentClone, targetClone);

              this.updateMedia(html, variant.featured_media.id);
              syncFirstSlide(newSlide);

              const firstThumb = mediaGallery.elements?.thumbnails?.querySelector('[data-target]');
              if (firstThumb && firstThumb.dataset.target !== hiddenVariantMediaId) {
                const newThumb = html.querySelector(`[data-target="${hiddenVariantMediaId}"]`);
                if (newThumb) {
                  firstThumb.innerHTML = newThumb.innerHTML;
                  firstThumb.dataset.target = hiddenVariantMediaId;

                  const oldBtn = firstThumb.querySelector('button');
                  if (oldBtn) {
                    const newBtn = oldBtn.cloneNode(true);
                    newBtn.removeAttribute('aria-current');
                    oldBtn.replaceWith(newBtn);
                    newBtn.addEventListener('click', () =>
                      mediaGallery.setActiveMedia(hiddenVariantMediaId, false, false)
                    );
                    newBtn.addEventListener('mouseenter', () =>
                      mediaGallery.preloadThumbnailImage?.(hiddenVariantMediaId)
                    );
                  }
                }
              }

              sliderComponent.reInit?.();

              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  Object.assign(currentClone.style, { opacity: '0', transform: 'scale(0.95)' });
                  Object.assign(targetClone.style, { opacity: '1', transform: 'scale(1)' });

                  let cleaned = false;
                  const cleanup = () => {
                    if (cleaned) return;
                    if (requestId !== this.variantRequestId || requestId !== this.mediaSyncToken) {
                      currentClone.remove();
                      targetClone.remove();
                      cleaned = true;
                      return;
                    }
                    cleaned = true;
                    currentClone.remove();
                    targetClone.remove();
                    this.triggerSlideAnimation(mediaGallery, hiddenVariantMediaId);
                    mediaGallery.setActiveMedia?.(hiddenVariantMediaId, false, false);
                  };

                  targetClone.addEventListener('transitionend', (e) => {
                    if (e.target === targetClone) cleanup();
                  }, { once: true });

                  setTimeout(cleanup, duration + 80);
                });
              });

            } else {
              this.updateMedia(html, variant.featured_media.id);
              syncFirstSlide(html.querySelector(`[data-media-id="${hiddenVariantMediaId}"]`));
              if (currentSrc !== targetSrc) {
                this.triggerSlideAnimation(mediaGallery, hiddenVariantMediaId);
                mediaGallery.setActiveMedia?.(hiddenVariantMediaId, false, false);
              }
              sliderComponent?.reInit?.();
            }

          } else {
            this.syncMedia(mediaGallery, variant, html, isMobile, isColorChange, requestId);
          }
        } else if (hasMedia) {
          this.syncMedia(mediaGallery, variant, html, isMobile, isColorChange, requestId);
        } else {
          this.updateMedia(html, variant?.featured_media?.id);
        }

        const updateSourceFromDestination = (id, shouldHide = () => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('price');
        updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('Badges', ({ innerText }) => innerText === '');
        updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
        updateSourceFromDestination('Attributes');
        updateSourceFromDestination('Volume');
        updateSourceFromDestination('PickupAvailability');
        updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

        this.updateQuantityRules(this.sectionId, html);
        this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
        this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

        this.productForm?.toggleSubmitButton?.(
          html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
          window.variantStrings.soldOut
        );

        if (variant?.available) {
          this.productForm?.updateButtonPrice(variant);
        }

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html,
            variant,
          },
        });

        return true;
      };
    }

    updateVariantInputs(variantId) {
      const forms = this.querySelectorAll(
        `#product-form-${this.dataset.section}-${this.dataset.productId}, #product-form-installment-${this.dataset.section}-${this.dataset.productId}`
      );
      forms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId ?? '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateURL(variantId) {
      const newPath = `${this.dataset.url}${variantId ? `?variant=${variantId}` : ''}`;
      this.querySelector('share-button')?.updateUrl(
        `${window.shopUrl}${newPath}`
      );

      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', newPath);
    }

    setUnavailable() {
      this.productForm?.toggleSubmitButton?.(true, window.variantStrings.unavailable);

      const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
        .map((id) => `#${id}-${this.dataset.section}`)
        .join(', ');
      document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
    }

    updateMedia(html, variantFeaturedMediaId) {
      if (!variantFeaturedMediaId) return;

      const removeQuickviewPrefix = id => id?.replace(/^quickview-/, '') || id;

      const mediaGallerySource = this.querySelector('media-gallery ul');
      const mediaGalleryDestination = html.querySelector('media-gallery ul');

      if (!mediaGallerySource || !mediaGalleryDestination) return;

      const refreshSourceData = () => {
        const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
        const sourceSet = new Set(mediaGallerySourceItems.map((item) => removeQuickviewPrefix(item.dataset.mediaId)));
        const sourceMap = new Map(
          mediaGallerySourceItems.map((item, index) => [removeQuickviewPrefix(item.dataset.mediaId), { item, index }])
        );
        return [mediaGallerySourceItems, sourceSet, sourceMap];
      };

      let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
      const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
      const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => removeQuickviewPrefix(dataset.mediaId)));

      let shouldRefresh = false;

      for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
        const destId = removeQuickviewPrefix(mediaGalleryDestinationItems[i].dataset.mediaId);
        if (!sourceSet.has(destId)) {
          mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
          shouldRefresh = true;
        }
      }

      for (let i = 0; i < mediaGallerySourceItems.length; i++) {
        const srcId = removeQuickviewPrefix(mediaGallerySourceItems[i].dataset.mediaId);
        if (!destinationSet.has(srcId)) {
          mediaGallerySourceItems[i].remove();
          shouldRefresh = true;
        }
      }

      if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

      mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
        const destId = removeQuickviewPrefix(destinationItem.dataset.mediaId);
        const sourceData = sourceMap.get(destId);

        if (sourceData && sourceData.index !== destinationIndex) {
          mediaGallerySource.insertBefore(
            sourceData.item,
            mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
          );

          [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        }
      });

      Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]')).forEach((li, i) => {
        li.dataset.mediaPosition = i + 1;
      });

      const thumbList = this.querySelector('[id^="GalleryThumbnails"] .thumbnail-list');
      if (thumbList) {
        const positionByMediaId = new Map(
          Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]')).map(li => [
            removeQuickviewPrefix(li.dataset.mediaId),
            li.dataset.mediaPosition
          ])
        );
        thumbList.querySelectorAll('[data-target]').forEach(thumb => {
          const id = removeQuickviewPrefix(thumb.dataset.target);
          const pos = positionByMediaId.get(id);
          if (pos !== undefined) thumb.dataset.mediaPosition = pos;
        });
      }

      const mediaGallery = this.querySelector('media-gallery');
      if (mediaGallery) {
        mediaGallerySource.querySelectorAll('img[data-src]').forEach(img => {
          if (typeof mediaGallery.preloadImage === 'function') {
            mediaGallery.preloadImage(img);
          } else {
            if (img.dataset.src) {
              img.src = img.dataset.src;
              delete img.dataset.src;
            }
            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
              delete img.dataset.srcset;
            }
          }
        });
      }

      const modalList = this.productModal?.querySelector('.gallery__media-list');
      const newModalList = html.querySelector('gallery-modal .gallery__media-list');
      if (modalList && newModalList) {
        modalList.innerHTML = newModalList.innerHTML;

        const productModal = this.productModal;
        if (productModal) {
          const modalSlider = productModal.querySelector('slider-component');
          if (modalSlider) {
            modalSlider.sliderItems = modalSlider.sliderTrack?.querySelectorAll(':scope > .slide');
            modalSlider.reInit?.();
          }
          if (productModal.isOpen) {
            productModal.destroyPinchZoom?.();
            productModal.initPinchZoom?.();
          }
        }
      }
    }

    setQuantityBoundries() {
      const data = {
        cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
        min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
        max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
        step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
      };

      let min = data.min;
      const max = data.max === null ? data.max : data.max - data.cartQuantity;
      if (max !== null) min = Math.min(min, max);
      if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

      this.quantityInput.min = min;

      if (max) {
        this.quantityInput.max = max;
      } else {
        this.quantityInput.removeAttribute('max');
      }
      this.quantityInput.value = min;

      const quantityInput = this.quantityInput.closest('quantity-input');
      if (quantityInput && typeof quantityInput.onInputChange === 'function') {
        quantityInput.onInputChange();
      }

      publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
    }

    fetchQuantityRules() {
      const currentVariantId = this.productForm?.variantIdInput?.value;
      if (!currentVariantId) return;

      const loadingSpinner = this.querySelector('.quantity__rules-cart .loading-spinner');
      loadingSpinner?.classList.remove('hidden');
      return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = this.parseSectionHtml(responseText);
          this.updateQuantityRules(this.dataset.section, html);
        })
        .catch((e) => console.error(e))
        .finally(() => loadingSpinner?.classList.add('hidden'));
    }

    updateQuantityRules(sectionId, html) {
      if (!this.quantityInput || !this.quantityForm) return;
      this.setQuantityBoundries();

      const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
      if (!quantityFormUpdated) return;
      const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
      for (let selector of selectors) {
        const current = this.quantityForm.querySelector(selector);
        const updated = quantityFormUpdated.querySelector(selector);
        if (!current || !updated) continue;
        if (selector === '.quantity__input') {
          const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
          for (let attribute of attributes) {
            const valueUpdated = updated.getAttribute(attribute);
            if (valueUpdated !== null) {
              current.setAttribute(attribute, valueUpdated);
            } else {
              current.removeAttribute(attribute);
            }
          }
        } else {
          current.innerHTML = updated.innerHTML;
        }
      }
    }

    parseSectionHtml(responseText) {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const lazyTemplates = Array.from(html.querySelectorAll('lazy-section-template > template'));

      for (const template of lazyTemplates) {
        const lazyContainer = template.closest('lazy-section-template');
        if (!lazyContainer) continue;
        const fragment = template.content?.cloneNode(true);
        if (!fragment) continue;
        lazyContainer.replaceWith(fragment);
      }

      return html;
    }

    get productForm() {
      return this.querySelector(`product-form`);
    }

    get productModal() {
      return document.querySelector(`#ProductModal-${this.dataset.section}`);
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get variantSelectors() {
      return this.querySelector('variant-selects');
    }

    get relatedProducts() {
      const relatedProductsSectionId = SectionId.getIdForSection(
        SectionId.parseId(this.sectionId),
        'related-products'
      );
      return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
    }

    get quickOrderList() {
      const quickOrderListSectionId = SectionId.getIdForSection(
        SectionId.parseId(this.sectionId),
        'quick_order_list'
      );
      return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.section;
    }
  });
}
